import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/components/context/AuthContext";
import { GoogleMapsProvider } from "@/components/context/GoogleMapsContext";
import { AcademicYearProvider } from "@/contexts/AcademicYearContext";
import { AdminSchoolProvider } from "@/contexts/AdminSchoolContext";
import { useEffect, lazy, Suspense, useState } from "react";
import { EnhancedErrorBoundary } from "@/components/ui/enhanced-error-boundary";
import { ChunkLoadErrorBoundary } from "@/components/error-boundary/ChunkLoadErrorBoundary";
import { setupDistrictBoundariesTable, setupGeospatialFunctions } from './utils/setupDistrictBoundaries';
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { NavigationProvider } from '@/components/context/NavigationContext';
import { HelmetProvider } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/components/context/AuthContext';
import { QuickSupportModal } from '@/components/school/support/QuickSupportModal';
import LegacyApplyRedirect from '@/components/application/LegacyApplyRedirect';
import LegacyEditRedirect from '@/components/application/LegacyEditRedirect';
import type { HelpEscalationContext, SupportRequest } from '@/types/support';
import { useSchoolSupport } from '@/hooks/useSchoolSupport';
import { ErrorTrackingInitializer } from '@/components/ErrorTrackingInitializer';
import { retryDynamicImport } from '@/utils/chunk-retry';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';
import { ScrollToTop } from '@/components/utils/ScrollToTop';
import { ReactRouterAnalyticsProvider } from '@/components/providers/react-router-analytics-provider';
import { useBoundaryCache } from '@/hooks/useBoundaryCache';
import { invalidateEnrollmentQueries } from '@/utils/enrollment-refresh';

// Lazy load all route components with retry mechanism
const Index = lazy(() => retryDynamicImport(() => import("./pages/Index"), "Index"));
const AdminPage = lazy(() => retryDynamicImport(() => import("./pages/AdminPage"), "AdminPage"));
const SchoolPage = lazy(() => retryDynamicImport(() => import("./pages/SchoolPage"), "SchoolPage"));
const TrackPage = lazy(() => retryDynamicImport(() => import("./pages/TrackPageMinimal"), "TrackPage"));
const DebugDbPage = lazy(() => retryDynamicImport(() => import("./pages/DebugDbPage"), "DebugDbPage"));
const AuthPage = lazy(() => retryDynamicImport(() => import("./pages/AuthPageMinimal"), "AuthPage"));
const NotFound = lazy(() => retryDynamicImport(() => import("./pages/NotFound"), "NotFound"));
const ProfilePage = lazy(() => retryDynamicImport(() => import("./pages/ProfileMinimal"), "ProfilePage"));
const Contact = lazy(() => retryDynamicImport(() => import("./pages/ContactMinimal"), "Contact"));
const Policy = lazy(() => retryDynamicImport(() => import("./pages/PolicyMinimal"), "Policy"));
const ConfirmationPage = lazy(() => retryDynamicImport(() => import("./pages/ConfirmationPageMinimal"), "ConfirmationPage"));
// Staff-only help system: its own chunk, so public visitors never download the help articles.
const HelpProvider = lazy(() => retryDynamicImport(() => import('@/components/help/HelpProvider').then(m => ({ default: m.HelpProvider })), "HelpProvider"));
const SchoolDistrictMap = lazy(() => retryDynamicImport(() => import("./pages/SchoolDistrictMap"), "SchoolDistrictMap"));
const EditApplicationPage = lazy(() => retryDynamicImport(() => import('./components/application/EditApplicationPage'), "EditApplicationPage"));
const RequestEditLinkPage = lazy(() => retryDynamicImport(() => import('./pages/RequestEditLinkPage'), "RequestEditLinkPage"));
const SchoolPulseDetail = lazy(() => retryDynamicImport(() => import("./pages/SchoolPulseDetail"), "SchoolPulseDetail"));
const InvitationAcceptPage = lazy(() => retryDynamicImport(() => import("./pages/InvitationAcceptPage"), "InvitationAcceptPage"));
const AuthCallbackPage = lazy(() => retryDynamicImport(() => import("./pages/AuthCallbackPage"), "AuthCallbackPage"));
const AboutUs = lazy(() => retryDynamicImport(() => import("./pages/AboutUs"), "AboutUs"));
const TermsOfService = lazy(() => retryDynamicImport(() => import("./pages/TermsOfServiceMinimal"), "TermsOfService"));
const Accessibility = lazy(() => retryDynamicImport(() => import("./pages/Accessibility"), "Accessibility"));
const Privacy = lazy(() => retryDynamicImport(() => import("./pages/PrivacyMinimal"), "Privacy"));
const LoadingDemoSimple = lazy(() => retryDynamicImport(() => import("./pages/LoadingDemoSimple"), "LoadingDemoSimple"));
const LoadingBestPractices = lazy(() => retryDynamicImport(() => import("./pages/LoadingBestPractices"), "LoadingBestPractices"));
const TransportationPortal = lazy(() => retryDynamicImport(() => import("./pages/transportation-portal"), "TransportationPortal"));
const ProgressDemo = lazy(() => retryDynamicImport(() => import("./pages/progress-demo"), "ProgressDemo"));
const TestProgressBar = lazy(() => retryDynamicImport(() => import("./pages/test-progress-bar"), "TestProgressBar"));
const BulkOperationExample = lazy(() => retryDynamicImport(() => import("./components/admin/bulk-operation-example").then(m => ({ default: m.BulkOperationExample })), "BulkOperationExample"));
const MapSkeletonDemo = lazy(() => retryDynamicImport(() => import("./pages/MapSkeletonDemo"), "MapSkeletonDemo"));
const AnalyticsTestPage = lazy(() => retryDynamicImport(() => import("./pages/AnalyticsTestPage"), "AnalyticsTestPage"));
const ErrorTrackingTest = lazy(() => retryDynamicImport(() => import("./pages/ErrorTrackingTest"), "ErrorTrackingTest"));
const SimpleErrorTest = lazy(() => retryDynamicImport(() => import("./pages/SimpleErrorTest"), "SimpleErrorTest"));
const ClassicApplyPage = lazy(() => retryDynamicImport(() => import("./pages/ClassicApplyPage"), "ClassicApplyPage"));
const PreviewApplyPage = lazy(() => retryDynamicImport(() => import("./pages/PreviewApplyPage"), "PreviewApplyPage"));
const ModernDesignDemo = lazy(() => retryDynamicImport(() => import("./pages/ModernDesignDemo"), "ModernDesignDemo"));
const ContributorReportPage = lazy(() => retryDynamicImport(() => import("./pages/ContributorReportPage"), "ContributorReportPage"));

// Partner Portal
const PartnerPortal = lazy(() => retryDynamicImport(() => import("./pages/PartnerPortal"), "PartnerPortal"));

// District Admin Pages
const DistrictAdminPage = lazy(() => retryDynamicImport(() => import("./pages/DistrictAdminPage"), "DistrictAdminPage"));
const DistrictSchoolDetailPage = lazy(() => retryDynamicImport(() => import("./pages/DistrictSchoolDetailPage"), "DistrictSchoolDetailPage"));
const DistrictSelector = lazy(() => retryDynamicImport(() => import("./components/district-admin").then(m => ({ default: m.DistrictSelector })), "DistrictSelector"));

// Create QueryClient outside component to avoid recreation on renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false, // Prevent refetch on tab focus
    },
  },
});

// Export queryClient for force refresh utility
export { queryClient };

// Setup global listener for cache clear events (from force refresh utility)
if (typeof window !== 'undefined') {
  window.addEventListener('app:clear-all-caches', () => {
    console.log('[App] Clearing React Query cache...');
    queryClient.clear();
  });

  // Bridge enrollment changes (rollup / rollback / manual-rollup) to React Query.
  // triggerEnrollmentRefresh() dispatches this event but previously only components that
  // explicitly subscribed via useEnrollmentRefresh() reacted — the React Query cache itself
  // was never invalidated, so other views kept serving stale, pre-rollup enrollment data.
  // This central listener guarantees the cache is invalidated for every enrollment operation.
  window.addEventListener('enrollment-data-changed', () => {
    console.log('[App] Enrollment data changed — invalidating React Query cache...');
    invalidateEnrollmentQueries(queryClient);
  });

  // Bridge capacity edits to React Query. SchoolCapacityManagementV2 already dispatches
  // 'capacity-updated' after upsert_school_grade_capacity (3 edit sites), but only refreshed
  // its own local state — React Query consumers (PlacementProcessor, Analytics, capacity
  // visualizations) kept serving stale capacity. Capacity feeds placement/lottery decisions,
  // so stale capacity is a correctness risk. Invalidate the capacity query family (partial
  // match covers every academic-year-scoped ['school-capacity', year] key).
  window.addEventListener('capacity-updated', () => {
    console.log('[App] Capacity updated — invalidating school-capacity queries...');
    queryClient.invalidateQueries({ queryKey: ['school-capacity'] });
  });
}

// Loading component for Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Conditional Help Provider - only shows for authenticated school staff
const ConditionalHelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userRoles, isLoading } = useAuth();
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [helpContext, setHelpContext] = useState<HelpEscalationContext | undefined>();
  const { createRequest } = useSchoolSupport();
  
  // Define roles that should have access to help system
  const staffRoles = ['school_staff', 'admin', 'district_admin', 'principal', 'assistant_principal', 'teacher', 'counselor', 'secretary'];
  
  // Check if user has any staff role
  const isStaffUser = user && userRoles.some(role => staffRoles.includes(role));
  
  // Don't show help system during loading or for non-staff users
  if (isLoading || !isStaffUser) {
    return <>{children}</>;
  }
  
  // Determine user role for help content filtering
  const helpUserRole = userRoles.includes('admin') || userRoles.includes('district_admin') ? 'administrator' :
                      userRoles.includes('principal') || userRoles.includes('assistant_principal') ? 'administrator' :
                      userRoles.includes('teacher') || userRoles.includes('counselor') ? 'coordinator' :
                      userRoles.includes('secretary') ? 'office-staff' :
                      'all-staff';
  
  const handleSupportSubmit = async (request: Partial<SupportRequest> & { subject?: string }) => {
    // Map the request data to match the expected format for createRequest
    await createRequest.mutateAsync({
      category: request.category || 'other',
      priority: request.priority || 'medium',
      subject: request.subject || '',
      description: request.description || '',
      screenshots: request.attachments as string[] || [],
      metadata: {
        helpContext: helpContext,
        source: 'help_system'
      }
    });
  };
  
  return (
    <>
      <Suspense fallback={children}>
      <HelpProvider
        initialUserRole={helpUserRole}
        enableFloatingButton={false}
        enableAnalytics={true}
        onSupportEscalation={(context) => {
          console.log('Support request:', context);
          // Set the help context and open the support modal
          setHelpContext(context);
          setSupportModalOpen(true);
        }}
      >
        {children}
      </HelpProvider>
      </Suspense>
      
      {/* Support Request Modal */}
      <QuickSupportModal
        isOpen={supportModalOpen}
        onClose={() => {
          setSupportModalOpen(false);
          setHelpContext(undefined);
        }}
        schoolId={user?.user_metadata?.school_id}
        userId={user?.id}
        helpContext={helpContext}
        onSubmit={handleSupportSubmit}
      />
    </>
  );
};

const App = () => {
  // Initialize boundary cache management
  useBoundaryCache();
  
  // Initialize on app load - defer non-critical setup
  useEffect(() => {
    // Auto-reload on update disabled per user request

    // Defer district boundaries setup to not block initial render
    const timer = setTimeout(() => {
      Promise.all([
        setupDistrictBoundariesTable(),
        setupGeospatialFunctions()
      ]).catch(err => {
        console.error('Boundary setup error:', err);
        EnhancedErrorLogger.getInstance().logQuickError(err, 'App', { context: 'boundary and geospatial setup' });
      });
    }, 1000); // Delay by 1 second to allow critical content to load first
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <EnhancedErrorBoundary componentName="AppRoot">
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
        <NavigationProvider>
          <AuthProvider>
              <AcademicYearProvider>
                <AdminSchoolProvider>
                  <GoogleMapsProvider>
                  <TooltipProvider>
                          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                            <ReactRouterAnalyticsProvider>
                              <ScrollToTop />
                              <ErrorTrackingInitializer>
                              <ConditionalHelpProvider>
                                <ChunkLoadErrorBoundary>
                                  <Suspense fallback={<PageLoader />}>
                                    <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/admin" element={
                            <ProtectedRoute allowedRoles={['admin', 'district_admin']}>
                              <AdminPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/school" element={
                            <ProtectedRoute allowedRoles={['school_staff']}>
                              <SchoolPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/track" element={<TrackPage />} />
                          <Route path="/debug-db" element={
                            <ProtectedRoute allowedRoles={['admin', 'district_admin']}>
                              <DebugDbPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/policy" element={<Policy />} />
                          <Route path="/about" element={<AboutUs />} />
                          <Route path="/terms" element={<TermsOfService />} />
                          <Route path="/accessibility" element={<Accessibility />} />
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/auth" element={<AuthPage />} />
                          <Route path="/invitation" element={<InvitationAcceptPage />} />
                          <Route path="/auth/callback" element={<AuthCallbackPage />} />
                          <Route path="/profile" element={
                            <ProtectedRoute>
                              <ProfilePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/confirmation/:confirmationCode" element={<ConfirmationPage />} />
                          <Route path="/district-map" element={<SchoolDistrictMap />} />
                          <Route path="/edit/:confirmationCode" element={<LegacyEditRedirect />} />
                          <Route path="/edit-link/request" element={<RequestEditLinkPage />} />
                          <Route path="/edit-link/:token" element={<EditApplicationPage />} />
                          <Route path="/report/:token" element={<ContributorReportPage />} />
                          <Route path="/admin/pulse/:schoolId" element={
                            <ProtectedRoute allowedRoles={['admin', 'district_admin']}>
                              <SchoolPulseDetail />
                            </ProtectedRoute>
                          } />
                          <Route path="/loading-demo" element={<LoadingDemoSimple />} />
                          <Route path="/loading-best" element={<LoadingBestPractices />} />
                          <Route path="/transportation-portal" element={
                            <ProtectedRoute>
                              <TransportationPortal />
                            </ProtectedRoute>
                          } />
                          <Route path="/progress-demo" element={<ProgressDemo />} />
                          <Route path="/test-progress-bar" element={<TestProgressBar />} />
                          <Route path="/bulk-operation-example" element={<BulkOperationExample />} />
                          <Route path="/map-skeleton-demo" element={<MapSkeletonDemo />} />
                          <Route path="/error-tracking-test" element={<ErrorTrackingTest />} />
                          <Route path="/simple-error-test" element={<SimpleErrorTest />} />
                          <Route path="/analytics-test" element={
                            <ProtectedRoute allowedRoles={['admin', 'district_admin']}>
                              <AnalyticsTestPage />
                            </ProtectedRoute>
                          } />
                          {/* Retired interactive form: old links land on the regular family form, query string intact */}
                          <Route path="/apply" element={<LegacyApplyRedirect />} />
                          <Route path="/apply-conversational" element={<LegacyApplyRedirect />} />
                          <Route path="/apply-classic" element={<ClassicApplyPage />} />
                          <Route path="/apply/preview" element={<PreviewApplyPage />} />
                          <Route path="/modern-demo" element={<ModernDesignDemo />} />
                          
                          {/* Partner Portal */}
                          <Route path="/partner" element={
                            <ProtectedRoute allowedRoles={['district_partner', 'admin']}>
                              <PartnerPortal />
                            </ProtectedRoute>
                          } />

                          {/* District Admin Routes */}
                          <Route path="/district-admin" element={
                            <ProtectedRoute>
                              <DistrictSelector />
                            </ProtectedRoute>
                          } />
                          <Route path="/district-admin/select" element={
                            <ProtectedRoute>
                              <DistrictSelector />
                            </ProtectedRoute>
                          } />
                          <Route path="/district-admin/:districtId" element={
                            <ProtectedRoute>
                              <DistrictAdminPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/district-admin/:districtId/schools/:schoolId" element={
                            <ProtectedRoute>
                              <DistrictSchoolDetailPage />
                            </ProtectedRoute>
                          } />
                          
                          <Route path="*" element={<NotFound />} />
                            </Routes>
                                </Suspense>
                              </ChunkLoadErrorBoundary>
                              <Toaster />
                              <Sonner />
                            </ConditionalHelpProvider>
                            </ErrorTrackingInitializer>
                            </ReactRouterAnalyticsProvider>
                          </Router>
                </TooltipProvider>
                  </GoogleMapsProvider>
                </AdminSchoolProvider>
            </AcademicYearProvider>
          </AuthProvider>
        </NavigationProvider>
      </QueryClientProvider>
    </HelmetProvider>
    </EnhancedErrorBoundary>
  );
};

export default App;
